import React, { useEffect, useRef, useState, useCallback } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Task, Collection, Phase } from '../types';
import {
  getProjectTasks, getProjectCollections,
  updateTaskCollection, updateTaskSortOrder, updateCollection, deleteCollection,
} from '../services/taskService';
import { dvList } from '../api/dataverseClient';
import TaskCard from '../task/TaskCard';
import TaskDetailDrawer from '../task/TaskDetailDrawer';
import NewTaskModal from '../task/NewTaskModal';
import { BoardGroupBy } from '../views/project/tabs/TasksTab';
import { getUserCardStates } from '../services/cardStateService';
import { primeServerPinState, getMyOid } from '../task/cardPinState';
import { getPersonalCollectionOrder, setPersonalCollectionOrder } from '../services/boardViewService';
import { useTaskPolling, mergeTaskDeltas, mergeCollectionDeltas } from '../hooks/useTaskPolling';

// Approved swatch palette for the color picker — exactly 20.
const DAGRUN_COLORS: { hex: string; label: string }[] = [
  { hex: '#0E7490', label: 'Nordic Steel' },
  { hex: '#0A5568', label: 'Steel Deep' },
  { hex: '#17A2C0', label: 'Steel Bright' },
  { hex: '#111318', label: 'Ink Black' },
  { hex: '#6D28D9', label: 'Violet' },
  { hex: '#4C1D95', label: 'Violet Deep' },
  { hex: '#0D6E6E', label: 'Teal Green' },
  { hex: '#065F5F', label: 'Teal Green Deep' },
  { hex: '#DC2626', label: 'Red' },
  { hex: '#991B1B', label: 'Red Deep' },
  { hex: '#7C3AED', label: 'Deep Violet' },
  { hex: '#5B21B6', label: 'Deep Violet Dark' },
  { hex: '#D97706', label: 'Amber' },
  { hex: '#92400E', label: 'Amber Deep' },
  { hex: '#374151', label: 'Slate' },
  { hex: '#1F2937', label: 'Slate Deep' },
  { hex: '#059669', label: 'Emerald' },
  { hex: '#047857', label: 'Emerald Deep' },
  { hex: '#475569', label: 'Blue Slate' },
  { hex: '#334155', label: 'Blue Slate Deep' },
];

interface Props { pca: IPublicClientApplication; projectId: string; refreshKey?: number; groupBy?: BoardGroupBy; agile?: boolean; }

type GroupBy = BoardGroupBy;

const UNASSIGNED = '__unassigned';

// ── Default sort = soonest due date ──────────────────────────────────
// A collection sorts by due date (ascending, undated tasks last) by DEFAULT.
// The moment the user drags a card within/into a collection, that collection
// is flagged as "manually ordered" and thereafter renders by the persisted
// dagrun_pm_sortorder instead — so an explicit hand-arranged order is never
// overridden. The flag is personal (localStorage), mirroring the app's other
// personal board-view state (collection order, card pin state).
const MANUAL_SORT_KEY = 'dagrun_pm_board_manualsort';

function manualSortSet(projectId: string): Set<string> {
  try {
    const raw = window.localStorage.getItem(`${MANUAL_SORT_KEY}_${projectId}`);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as string[];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch { return new Set(); }
}

function markManualSort(projectId: string, collectionIds: string[]): void {
  try {
    const set = manualSortSet(projectId);
    collectionIds.forEach(id => set.add(id));
    window.localStorage.setItem(`${MANUAL_SORT_KEY}_${projectId}`, JSON.stringify([...set]));
  } catch { /* localStorage unavailable — falls back to due-date default */ }
}

// Compare two tasks by due date ascending; undated tasks sort last; ties fall
// back to createdon (stable, oldest first).
function compareByDueDate(a: Task, b: Task): number {
  const av = a.dagrun_pm_duedate ? new Date(a.dagrun_pm_duedate).getTime() : Infinity;
  const bv = b.dagrun_pm_duedate ? new Date(b.dagrun_pm_duedate).getTime() : Infinity;
  if (av !== bv) return av - bv;
  const ac = a.createdon ? new Date(a.createdon).getTime() : 0;
  const bc = b.createdon ? new Date(b.createdon).getTime() : 0;
  return ac - bc;
}

// Compare by persisted manual sortorder (ascending); ties fall back to createdon.
function compareBySortOrder(a: Task, b: Task): number {
  const av = a.dagrun_pm_sortorder ?? Number.MAX_SAFE_INTEGER;
  const bv = b.dagrun_pm_sortorder ?? Number.MAX_SAFE_INTEGER;
  if (av !== bv) return av - bv;
  const ac = a.createdon ? new Date(a.createdon).getTime() : 0;
  const bc = b.createdon ? new Date(b.createdon).getTime() : 0;
  return ac - bc;
}

// Default collection gradient colors when dagrun_pm_color is null.
const DEFAULT_HEADER_FROM = '#0E7490';
const DEFAULT_HEADER_TO   = '#0A5568';

// Darken a hex color by `amount` lightness points (0–100). Goes hex → HSL → reduce L → hex.
export function darkenHex(hex: string, amount = 15): string {
  const m = /^#?([a-f\d]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  const h = m[1];
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let s = 0, hue = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r)      hue = ((g - b) / d + (g < b ? 6 : 0));
    else if (max === g) hue = ((b - r) / d + 2);
    else                hue = ((r - g) / d + 4);
    hue /= 6;
  }
  const newL = Math.max(0, Math.min(1, l - amount / 100));
  // HSL → RGB
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  let nr: number, ng: number, nb: number;
  if (s === 0) { nr = ng = nb = newL; }
  else {
    const q = newL < 0.5 ? newL * (1 + s) : newL + s - newL * s;
    const p = 2 * newL - q;
    nr = hue2rgb(p, q, hue + 1 / 3);
    ng = hue2rgb(p, q, hue);
    nb = hue2rgb(p, q, hue - 1 / 3);
  }
  const toHex = (n: number) => Math.round(n * 255).toString(16).padStart(2, '0');
  return `#${toHex(nr)}${toHex(ng)}${toHex(nb)}`;
}

export default function BoardView({ pca, projectId, refreshKey, groupBy = 'status', agile }: Props) {
  const [tasks, setTasks]             = useState<Task[]>([]);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [phases, setPhases]           = useState<Phase[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [newTaskColId, setNewTaskColId] = useState<string | undefined>();
  const [newTaskSortOrder, setNewTaskSortOrder] = useState<number | undefined>();
  // Delete-collection modal state — declared at the top to keep hook order stable.
  const [deletingId, setDeletingId]     = useState<string | null>(null);
  const [moveDestId, setMoveDestId]     = useState<string>('');
  const [deleting, setDeleting]         = useState(false);
  const [deleteError, setDeleteError]   = useState<string | null>(null);
  // True from drag start until the drop's PATCHes settle — polling merges
  // are deferred for the whole window so a stale server row can't briefly
  // snap the dragged card back.
  const [dragging, setDragging]         = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const myOid = getMyOid(pca);
      const [t, c, p, personalOrder] = await Promise.all([
        getProjectTasks(pca, projectId),
        getProjectCollections(pca, projectId),
        dvList<Phase>(pca, 'dagrun_pm_phases', {
          select: ['dagrun_pm_phaseid', 'dagrun_pm_name', 'dagrun_pm_sortorder'],
          filter: `_dagrun_pm_projectid_value eq ${projectId}`,
          orderby: 'dagrun_pm_sortorder asc',
        }).catch(() => [] as Phase[]),
        myOid ? getPersonalCollectionOrder(pca, projectId, myOid).catch(() => null) : Promise.resolve(null),
      ]);
      // If user has a saved personal collection order, apply it as the
      // render order via assigned sortorder (without persisting back to
      // dagrun_pm_collection — this stays personal).
      let cOrdered = c;
      if (personalOrder && personalOrder.length > 0) {
        const indexById = new Map<string, number>();
        personalOrder.forEach((id, i) => indexById.set(id, i));
        cOrdered = [...c].sort((a, b) => {
          const ai = indexById.has(a.dagrun_pm_collectionid) ? indexById.get(a.dagrun_pm_collectionid)! : 9999;
          const bi = indexById.has(b.dagrun_pm_collectionid) ? indexById.get(b.dagrun_pm_collectionid)! : 9999;
          return ai - bi;
        });
      }
      setTasks(t); setCollections(cOrdered); setPhases(p);
      // Load the user's server-persisted card expansion state in one
      // query and seed the pinstate cache. Do this AFTER tasks so the
      // prime broadcast arrives once cards are mounted and their
      // usePinState hooks re-read via the event handler.
      try {
        const myOid = getMyOid(pca);
        if (myOid) {
          const states = await getUserCardStates(pca, myOid);
          primeServerPinState(myOid, states);
        }
      } catch { /* non-fatal — localStorage fallback is still live */ }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load board');
    } finally {
      setLoading(false);
    }
  }, [pca, projectId]);

  useEffect(() => { loadData(); }, [loadData, refreshKey]);

  // Real-time refresh: other users' task/collection changes appear within
  // one polling interval. Deferred while anything edit-like is open.
  useTaskPolling({
    pca, projectId,
    isPaused: () => loading || dragging || selectedTask !== null
      || newTaskColId !== undefined || deletingId !== null,
    onTasks: deltas => setTasks(prev => mergeTaskDeltas(prev, deltas)),
    onCollections: deltas => setCollections(prev => mergeCollectionDeltas(prev, deltas)),
  });

  const handleTaskDeleted = (taskId: string) =>
    setTasks(prev => prev.filter(t => t.dagrun_pm_taskid !== taskId));

  if (loading) return <div className="spinner" style={{ margin: '80px auto' }} />;
  if (error)   return <div className="card" style={{ padding: 16, margin: 24, color: 'var(--danger)' }}>{error}</div>;

  // Bucket tasks by collection for render
  const byCollection: Record<string, Task[]> = {};
  collections.forEach(c => { byCollection[c.dagrun_pm_collectionid] = []; });
  byCollection[UNASSIGNED] = [];
  tasks.forEach(task => {
    const col = task._dagrun_pm_wipcollectionid_value;
    if (col && byCollection[col]) byCollection[col].push(task);
    else byCollection[UNASSIGNED].push(task);
  });
  // Order each collection: manually-arranged collections keep their persisted
  // sortorder; every other collection defaults to soonest-due-date-first.
  const manualCols = manualSortSet(projectId);
  Object.keys(byCollection).forEach(colId => {
    byCollection[colId].sort(manualCols.has(colId) ? compareBySortOrder : compareByDueDate);
  });

  const phaseNameById: Record<string, string> = {};
  phases.forEach(p => { phaseNameById[p.dagrun_pm_phaseid] = p.dagrun_pm_name; });

  const handleDragEnd = async (result: DropResult) => {
    try {
      await handleDragEndInner(result);
    } finally {
      setDragging(false);
    }
  };

  const handleDragEndInner = async (result: DropResult) => {
    const { source, destination, draggableId, type } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    // Collection reorder — dragging collection headers in the horizontal list.
    if (type === 'collection') {
      // draggableId is prefixed with "col-" to keep it distinct from task ids.
      const realId = draggableId.startsWith('col-') ? draggableId.slice(4) : draggableId;
      await handleCollectionReorder(realId, source.index, destination.index);
      return;
    }

    const taskId  = draggableId;
    const destCol = destination.droppableId;
    const srcCol  = source.droppableId;
    const sameCol = srcCol === destCol;

    const moved = tasks.find(t => t.dagrun_pm_taskid === taskId);
    if (!moved) return;

    // An explicit drag switches the affected collection(s) to manual ordering
    // so the hand-arranged order (persisted below via sortorder) is respected
    // on the next render instead of the due-date default.
    markManualSort(projectId, sameCol ? [destCol] : [srcCol, destCol]);

    const destListOld = tasks.filter(t =>
      (t._dagrun_pm_wipcollectionid_value ?? UNASSIGNED) === destCol
      && t.dagrun_pm_taskid !== taskId);
    const destList = [...destListOld];
    destList.splice(destination.index, 0, {
      ...moved,
      _dagrun_pm_wipcollectionid_value: destCol === UNASSIGNED ? null : destCol,
    });
    const renumberedDest = destList.map((t, i) => ({ ...t, dagrun_pm_sortorder: i }));

    let renumberedSrc: Task[] = [];
    if (!sameCol) {
      const srcList = tasks.filter(t =>
        (t._dagrun_pm_wipcollectionid_value ?? UNASSIGNED) === srcCol
        && t.dagrun_pm_taskid !== taskId);
      renumberedSrc = srcList.map((t, i) => ({ ...t, dagrun_pm_sortorder: i }));
    }

    const affectedIds = new Set([
      ...renumberedDest.map(t => t.dagrun_pm_taskid),
      ...renumberedSrc.map(t => t.dagrun_pm_taskid),
    ]);
    const kept = tasks.filter(t => !affectedIds.has(t.dagrun_pm_taskid));
    const newTasks = [...kept, ...renumberedDest, ...renumberedSrc];
    setTasks(newTasks);

    const oldBy = new Map<string, number | null>(
      tasks.map(t => [t.dagrun_pm_taskid, t.dagrun_pm_sortorder ?? null])
    );
    const changed = [...renumberedDest, ...renumberedSrc].filter(t =>
      (t.dagrun_pm_sortorder ?? null) !== (oldBy.get(t.dagrun_pm_taskid) ?? null));

    try {
      if (!sameCol) {
        await updateTaskCollection(pca, taskId, destCol === UNASSIGNED ? null : destCol);
      }
      // Persist sortorder for every changed task — no cap. Parallelize so a
      // long column move doesn't stall the UI behind sequential PATCHes.
      await Promise.all(changed.map(t =>
        updateTaskSortOrder(pca, t.dagrun_pm_taskid, t.dagrun_pm_sortorder ?? 0)
      ));
    } catch {
      loadData();
    }
  };

  const handleCollectionReorder = async (collectionId: string, fromIdx: number, toIdx: number) => {
    // Personal-view-scoped reorder: persists only for this user via
    // dagrun_pm_boardview.dagrun_pm_collectionorderjson. Other users keep
    // the global dagrun_pm_collection.dagrun_pm_sortorder.
    //
    // fromIdx/toIdx are positions WITHIN the active-columns list (the
    // sidebar at position 0 is not draggable). Reorder the active list,
    // then prepend any quiet ids so the persisted order keeps quiet
    // collections grouped at the start.
    const activeIds = activeCollections.map(c => c.dagrun_pm_collectionid);
    const newActiveOrder = [...activeIds];
    newActiveOrder.splice(fromIdx, 1);
    newActiveOrder.splice(toIdx, 0, collectionId);
    const stackedIds = stackedCollections.map(c => c.dagrun_pm_collectionid);
    const reordered = [...stackedIds, ...newActiveOrder];

    // Optimistic local re-order. Use a synthetic sortorder solely for
    // local sort; we don't PATCH this back to the collection record.
    const indexById = new Map<string, number>();
    reordered.forEach((id, i) => indexById.set(id, i));
    const updated = [...collections].sort((a, b) => {
      const ai = indexById.has(a.dagrun_pm_collectionid) ? indexById.get(a.dagrun_pm_collectionid)! : 9999;
      const bi = indexById.has(b.dagrun_pm_collectionid) ? indexById.get(b.dagrun_pm_collectionid)! : 9999;
      return ai - bi;
    });
    setCollections(updated);

    const myOid = getMyOid(pca);
    if (!myOid) return;
    try {
      await setPersonalCollectionOrder(pca, projectId, myOid, reordered);
    } catch {
      loadData();
    }
  };

  const handleColorChange = async (collectionId: string, hex: string) => {
    setCollections(cs => cs.map(c => c.dagrun_pm_collectionid === collectionId ? { ...c, dagrun_pm_color: hex } : c));
    try {
      await updateCollection(pca, collectionId, { dagrun_pm_color: hex });
    } catch {
      loadData();
    }
  };

  const handleRename = async (collectionId: string, newName: string) => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    const previous = collections.find(c => c.dagrun_pm_collectionid === collectionId)?.dagrun_pm_name;
    if (trimmed === previous) return;
    setCollections(cs => cs.map(c => c.dagrun_pm_collectionid === collectionId ? { ...c, dagrun_pm_name: trimmed } : c));
    try {
      await updateCollection(pca, collectionId, { dagrun_pm_name: trimmed });
    } catch {
      // revert
      if (previous != null) {
        setCollections(cs => cs.map(c => c.dagrun_pm_collectionid === collectionId ? { ...c, dagrun_pm_name: previous } : c));
      }
    }
  };

  const requestDelete = (collectionId: string) => {
    setDeletingId(collectionId);
    const otherCols = collections.filter(c => c.dagrun_pm_collectionid !== collectionId);
    setMoveDestId(otherCols[0]?.dagrun_pm_collectionid ?? '');
    setDeleteError(null);
  };

  const cancelDelete = () => {
    if (deleting) return;
    setDeletingId(null);
    setMoveDestId('');
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!deletingId) return;
    const tasksInCol = tasks.filter(t => t._dagrun_pm_wipcollectionid_value === deletingId);
    setDeleting(true);
    setDeleteError(null);
    try {
      // If there are tasks, move them all first.
      if (tasksInCol.length > 0) {
        if (!moveDestId) { setDeleteError('Pick a destination collection.'); setDeleting(false); return; }
        for (const t of tasksInCol) {
          await updateTaskCollection(pca, t.dagrun_pm_taskid, moveDestId);
        }
        // Optimistic local move
        setTasks(prev => prev.map(t => t._dagrun_pm_wipcollectionid_value === deletingId ? { ...t, _dagrun_pm_wipcollectionid_value: moveDestId } : t));
      }
      await deleteCollection(pca, deletingId);
      setCollections(cs => cs.filter(c => c.dagrun_pm_collectionid !== deletingId));
      setDeletingId(null);
      setMoveDestId('');
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Delete failed');
      // Reload to reconcile state on partial failure.
      loadData();
    } finally {
      setDeleting(false);
    }
  };

  const renderColumn = (id: string, name: string, items: Task[], wipLimit: number | null, color: string | null, dragHandleProps?: React.HTMLAttributes<HTMLDivElement>) => {
    const isUnassigned = id === UNASSIGNED;
    // Split into active vs done. Done = dagrun_pm_status === 100000003.
    const activeItems    = items.filter(t => t.dagrun_pm_status !== 100000003);
    const completedItems = items.filter(t => t.dagrun_pm_status === 100000003);

    return (
      <div
        key={id}
        style={{
          // 264 = 240 (TaskCard width) + 12px padding each side. Prevents
          // per-column horizontal scrollbars when cards sit flush.
          width: 264, flexShrink: 0,
          display: 'flex', flexDirection: 'column',
          maxHeight: 'calc(100vh - 240px)',
          background: '#FFFFFF',
          borderRadius: 8,
          boxShadow: '0 1px 3px rgba(15,17,23,0.06)',
          overflow: 'hidden',
        }}
      >
        <CollectionHeader
          id={id}
          name={name}
          color={color}
          completedCount={completedItems.length}
          totalCount={items.length}
          wipLimit={wipLimit}
          isUnassigned={isUnassigned}
          dragHandleProps={dragHandleProps}
          onColorChange={hex => handleColorChange(id, hex)}
          onRename={newName => handleRename(id, newName)}
          onRequestDelete={() => requestDelete(id)}
        />

        {/* Body container — 12px horizontal padding holds 240px card flush.
            Explicit overflowX: hidden because overflow-y: auto on the inner
            scroll area below would otherwise promote overflow-x from
            visible to auto (CSS spec: when one axis is scrolling and the
            other is visible, both become scrolling). Cards must never
            cause a per-column horizontal scrollbar — the BOARD scrolls
            horizontally, not individual columns. */}
        <div style={{ padding: 12, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflowX: 'hidden' }}>
          {!isUnassigned && (
            <button
              onClick={() => { setNewTaskColId(id); setNewTaskSortOrder(items.length); }}
              style={{
                flexShrink: 0, marginBottom: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                padding: '6px 8px',
                background: '#D4EEF5',
                border: '1px solid #0E7490',
                borderRadius: 6,
                fontFamily: 'var(--font-ui)',
                fontSize: 13, color: '#0E7490',
                cursor: 'pointer', transition: 'opacity 0.12s',
              }}
              onMouseEnter={e => { e.currentTarget.style.opacity = '0.8'; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
            >
              + Add task
            </button>
          )}

          {/* Active-tasks scroll area — only the active cards live here. The
              Completed disclosure is a sibling AFTER this div, pinned to the
              bottom of the column regardless of active-card scroll position.
              This structural separation is the regression fix: there's no
              code path where the Completed disclosure can render mid-stack
              inside the active list, because the two lists have different
              DOM parents. */}
          <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Droppable droppableId={id}>
              {(dropProvided, dropSnapshot) => (
                <div
                  ref={dropProvided.innerRef}
                  {...dropProvided.droppableProps}
                  style={{
                    minHeight: 40,
                    display: 'flex', flexDirection: 'column',
                    flexShrink: 0,
                    borderRadius: 6,
                    background: dropSnapshot.isDraggingOver ? '#D4EEF5' : 'transparent',
                    transition: 'background 0.12s',
                    position: 'relative',
                  }}
                >
                  {activeItems.length === 0 && completedItems.length === 0 && !dropSnapshot.isDraggingOver && (
                    <div style={{
                      padding: '16px 8px',
                      textAlign: 'center',
                      fontFamily: 'var(--font-ui)',
                      fontSize: 11,
                      color: '#B8BEC9',
                    }}>
                      No tasks
                    </div>
                  )}
                  {activeItems.map((t, index) => (
                    <Draggable key={t.dagrun_pm_taskid} draggableId={t.dagrun_pm_taskid} index={index}>
                      {(dragProvided, dragSnapshot) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          {...dragProvided.dragHandleProps}
                          style={{
                            ...dragProvided.draggableProps.style,
                            boxShadow: dragSnapshot.isDragging ? 'var(--shadow-panel)' : undefined,
                            opacity: dragSnapshot.isDragging ? 0.95 : 1,
                            cursor: dragSnapshot.isDragging ? 'grabbing' : 'pointer',
                          }}
                        >
                          <TaskCard
                            task={t}
                            onClick={setSelectedTask}
                            pca={pca}
                            phaseName={t._dagrun_pm_phaseid_value ? phaseNameById[t._dagrun_pm_phaseid_value] : undefined}
                            onDeleted={handleTaskDeleted}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {dropProvided.placeholder}
                </div>
              )}
            </Droppable>
          </div>

          {completedItems.length > 0 && (
            <div style={{
              flexShrink: 0,
              borderTop: '1px solid #EAECF0',
            }}>
              <CompletedSection
                items={completedItems}
                onTaskClick={setSelectedTask}
                pca={pca}
                phaseNameById={phaseNameById}
                onTaskDeleted={handleTaskDeleted}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  // Status mode: split collections into "quiet" (zero active tasks) and
  // "active" (1+ active task). Quiet collections do NOT render as
  // full-width board columns. Instead they stack into a single 264px
  // sidebar at Position 0 (leftmost), each row showing the collection name
  // and a "+ Add Task" affordance. As soon as a task is added (or moved
  // in) to a quiet collection, it leaves the sidebar and renders as a
  // normal column. When the last active task in a regular column is
  // completed or deleted, that collection becomes quiet again and
  // returns to the sidebar.
  const isActiveTask = (t: Task) => t.dagrun_pm_status !== 100000003;
  const activeCollections: Collection[] = [];
  const stackedCollections: Collection[] = [];
  collections.forEach(c => {
    const items = byCollection[c.dagrun_pm_collectionid] ?? [];
    (items.some(isActiveTask) ? activeCollections : stackedCollections).push(c);
  });
  // For drag/drop reorder we only operate on the active list — the sidebar
  // is a single fixed slot at position 0. orderedCollections is what the
  // collection-reorder DnD operates against.
  const orderedCollections: Collection[] = activeCollections;

  return (
    <DragDropContext onDragStart={() => setDragging(true)} onDragEnd={handleDragEnd}>
      <div style={{
        display: 'flex', flexDirection: 'column',
        height: '100%',
        background: 'var(--app-bg)',
        minHeight: 0,
        overflow: 'hidden',
      }}>
        {/* Board content — toolbar lives in TasksTab.
            Horizontal scrollbar must sit at the bottom of the viewport-bounded
            container (not below tall content), so the scroll container has
            minHeight:0 and flex:1 to stay viewport-sized regardless of column
            heights. */}
        {groupBy !== 'status' ? (
          <VirtualBoard tasks={tasks} phases={phases} groupBy={groupBy} onTaskClick={setSelectedTask} pca={pca} phaseNameById={phaseNameById} onTaskDeleted={handleTaskDeleted} />
        ) : (
          <div style={{
            flex: 1, minHeight: 0,
            overflowX: 'auto', overflowY: 'hidden',
            padding: 14,
          }}>
            <Droppable droppableId="__collections" direction="horizontal" type="collection">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  style={{ display: 'flex', gap: 12, alignItems: 'stretch', height: '100%' }}
                >
                  {stackedCollections.length > 0 && (
                    <StackedSidebar
                      collections={stackedCollections}
                      byCollection={byCollection}
                      onAddTask={(colId, count) => { setNewTaskColId(colId); setNewTaskSortOrder(count); }}
                      onTaskClick={setSelectedTask}
                      pca={pca}
                      phaseNameById={phaseNameById}
                      onTaskDeleted={handleTaskDeleted}
                    />
                  )}
                  {orderedCollections.map((c, index) => (
                    <Draggable key={c.dagrun_pm_collectionid} draggableId={`col-${c.dagrun_pm_collectionid}`} index={index}>
                      {(dragProvided, dragSnapshot) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          style={{
                            ...dragProvided.draggableProps.style,
                            boxShadow: dragSnapshot.isDragging ? 'var(--shadow-panel)' : undefined,
                            opacity: dragSnapshot.isDragging ? 0.95 : 1,
                          }}
                        >
                          {renderColumn(
                            c.dagrun_pm_collectionid,
                            c.dagrun_pm_name,
                            byCollection[c.dagrun_pm_collectionid] ?? [],
                            c.dagrun_pm_wiplimit,
                            c.dagrun_pm_color,
                            dragProvided.dragHandleProps ?? undefined,
                          )}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                  {byCollection[UNASSIGNED].length > 0 && renderColumn(UNASSIGNED, 'Unassigned', byCollection[UNASSIGNED], null, null)}
                </div>
              )}
            </Droppable>
          </div>
        )}
      </div>

      <TaskDetailDrawer
        task={selectedTask}
        pca={pca}
        onClose={() => setSelectedTask(null)}
        onSaved={(updated) => {
          // Targeted update: replace just the edited task in local state. No
          // full board reload — preserves column scroll position and avoids
          // re-rendering every column on every close.
          setTasks(prev => prev.map(t =>
            t.dagrun_pm_taskid === updated.dagrun_pm_taskid ? { ...t, ...updated } : t
          ));
        }}
        onDeleted={(taskId) => setTasks(prev => prev.filter(t => t.dagrun_pm_taskid !== taskId))}
      />

      {newTaskColId !== undefined && (
        <NewTaskModal
          pca={pca}
          projectId={projectId}
          collectionId={newTaskColId}
          initialSortOrder={newTaskSortOrder}
          agile={agile}
          onCreated={() => { setNewTaskColId(undefined); setNewTaskSortOrder(undefined); loadData(); }}
          onCancel={() => { setNewTaskColId(undefined); setNewTaskSortOrder(undefined); }}
        />
      )}

      {deletingId && (() => {
        const target  = collections.find(c => c.dagrun_pm_collectionid === deletingId);
        const inCount = tasks.filter(t => t._dagrun_pm_wipcollectionid_value === deletingId).length;
        const others  = collections.filter(c => c.dagrun_pm_collectionid !== deletingId);
        return (
          <div
            onClick={cancelDelete}
            style={{ position: 'fixed', inset: 0, background: 'rgba(15,17,23,0.35)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <div onClick={e => e.stopPropagation()} style={{
              width: 420, maxWidth: '92vw', background: '#FFFFFF', borderRadius: 10,
              boxShadow: '0 12px 40px rgba(15,17,23,0.25)', padding: 22,
              fontFamily: 'var(--font-ui)',
            }}>
              {inCount === 0 ? (
                <>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#0F1117', marginBottom: 8 }}>
                    Delete {target?.dagrun_pm_name ?? 'collection'}?
                  </div>
                  <div style={{ fontSize: 13, color: '#5A6272', lineHeight: 1.5, marginBottom: 18 }}>
                    This cannot be undone.
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#0F1117', marginBottom: 8 }}>
                    Move {inCount} task{inCount === 1 ? '' : 's'} before deleting {target?.dagrun_pm_name ?? 'collection'}
                  </div>
                  <div style={{ fontSize: 13, color: '#5A6272', lineHeight: 1.5, marginBottom: 14 }}>
                    Tasks must be reassigned to another collection before this one can be deleted.
                  </div>
                  <label style={{ display: 'block', fontSize: 12, color: '#5A6272', marginBottom: 4, fontWeight: 500 }}>
                    Move to
                  </label>
                  <select
                    value={moveDestId}
                    onChange={e => setMoveDestId(e.target.value)}
                    disabled={deleting || others.length === 0}
                    style={{ width: '100%', marginBottom: 16 }}
                  >
                    {others.length === 0 && <option value="">— No other collections —</option>}
                    {others.map(c => (
                      <option key={c.dagrun_pm_collectionid} value={c.dagrun_pm_collectionid}>{c.dagrun_pm_name}</option>
                    ))}
                  </select>
                </>
              )}

              {deleteError && (
                <div style={{ fontSize: 12, color: '#DC2626', marginBottom: 12 }}>{deleteError}</div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button className="btn-secondary" onClick={cancelDelete} disabled={deleting}>Cancel</button>
                <button
                  onClick={confirmDelete}
                  disabled={deleting || (inCount > 0 && !moveDestId)}
                  style={{
                    background: '#DC2626', color: '#fff', border: 'none',
                    borderRadius: 5, padding: '7px 14px',
                    fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 500,
                    cursor: deleting ? 'not-allowed' : 'pointer',
                    opacity: deleting ? 0.7 : 1,
                  }}
                >
                  {deleting ? 'Working…' : (inCount === 0 ? 'Delete' : 'Move tasks and delete')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </DragDropContext>
  );
}

// ── CollectionHeader ──────────────────────────────────────────────────
// Gradient header band with task count and (for non-Unassigned columns) a ••• menu
// containing Change color / Rename / Delete. Color picker shows the 20-swatch palette.

interface CollectionHeaderProps {
  id: string;
  name: string;
  color: string | null;
  completedCount: number;
  totalCount: number;
  wipLimit: number | null;
  isUnassigned: boolean;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
  onColorChange: (hex: string) => Promise<void> | void;
  onRename: (newName: string) => Promise<void> | void;
  onRequestDelete: () => void;
}

function CollectionHeader({ id: _id, name, color, completedCount, totalCount, wipLimit, isUnassigned, dragHandleProps, onColorChange, onRename, onRequestDelete }: CollectionHeaderProps) {
  const [hovered, setHovered]   = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState(name);
  const [saving, setSaving]     = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Reset rename draft if the underlying name changes.
  useEffect(() => { setRenameDraft(name); }, [name]);

  // Close any open popover when clicking outside this header (but not while renaming —
  // blur is already a save trigger and the input lives inside the header).
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (wrapRef.current.contains(e.target as Node)) return;
      setMenuOpen(false);
      setPickerOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const headerFrom = isUnassigned ? '#475569' : (color || DEFAULT_HEADER_FROM);
  const headerTo   = isUnassigned ? '#334155' : (color ? darkenHex(color, 15) : DEFAULT_HEADER_TO);

  const pickColor = async (hex: string) => {
    setPickerOpen(false);
    setSaving(true);
    try { await onColorChange(hex); } finally { setSaving(false); }
  };

  const startRename = () => {
    setRenameDraft(name);
    setRenaming(true);
    setMenuOpen(false);
  };

  const commitRename = async () => {
    if (!renaming) return;
    const next = renameDraft.trim();
    setRenaming(false);
    if (!next || next === name) return;
    setSaving(true);
    try { await onRename(next); } finally { setSaving(false); }
  };

  const cancelRename = () => {
    setRenameDraft(name);
    setRenaming(false);
  };

  return (
    <div
      ref={wrapRef}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      {...(dragHandleProps ?? {})}
      style={{
        position: 'relative',
        padding: '10px 12px',
        background: `linear-gradient(135deg, ${headerFrom}, ${headerTo})`,
        color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        flexShrink: 0,
        opacity: saving ? 0.6 : 1,
        transition: 'opacity 0.15s',
        cursor: dragHandleProps ? 'grab' : 'default',
      }}
    >
      {renaming ? (
        <input
          autoFocus
          value={renameDraft}
          onChange={e => setRenameDraft(e.target.value)}
          onBlur={commitRename}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
            if (e.key === 'Escape') { cancelRename(); }
          }}
          style={{
            flex: 1, minWidth: 0,
            background: 'transparent',
            color: '#fff',
            fontFamily: 'var(--font-ui)',
            fontSize: 11, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.08em',
            border: 'none',
            borderBottom: '1px solid rgba(255,255,255,0.5)',
            borderRadius: 0,
            outline: 'none',
            padding: '0 0 1px 0',
            boxShadow: 'none',
            width: '100%',
          }}
        />
      ) : (
        <span style={{
          fontFamily: 'var(--font-ui)',
          fontSize: 11, fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: '0.08em',
          color: '#fff',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          flex: 1, minWidth: 0,
        }}>
          {name}
        </span>
      )}

      <span style={{
        background: totalCount > 0 && completedCount === totalCount ? '#D1FAE5' : 'rgba(255,255,255,0.22)',
        color: totalCount > 0 && completedCount === totalCount ? '#065F46' : '#fff',
        fontSize: 10, fontWeight: 600,
        padding: '1px 8px',
        borderRadius: 10,
        fontFamily: 'var(--font-mono)',
        flexShrink: 0,
      }}
        title={wipLimit != null ? `${completedCount}/${totalCount} complete · WIP limit ${wipLimit}` : `${completedCount}/${totalCount} complete`}
      >
        {completedCount}/{totalCount}{wipLimit != null ? ` · ${wipLimit}` : ''}
      </span>

      {!isUnassigned && (
        <button
          type="button"
          onClick={e => { e.stopPropagation(); setMenuOpen(o => !o); setPickerOpen(false); }}
          aria-label="Collection options"
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: '#fff',
            opacity: hovered || menuOpen ? 1 : 0.7,
            padding: '0 2px', lineHeight: 1, fontSize: 16,
            fontFamily: 'var(--font-ui)',
            transition: 'opacity 0.12s',
          }}
        >⋯</button>
      )}

      {/* Dropdown menu */}
      {menuOpen && !isUnassigned && (
        <div style={{
          position: 'absolute', top: '100%', right: 8, marginTop: 4,
          background: '#FFFFFF',
          border: '1px solid #DDE0E6',
          borderRadius: 8,
          boxShadow: '0 4px 16px rgba(15,17,23,0.12)',
          minWidth: 140, zIndex: 50,
          padding: 4,
        }}>
          {[
            { key: 'color',  label: 'Change color' },
            { key: 'rename', label: 'Rename' },
            { key: 'delete', label: 'Delete', danger: true },
          ].map(item => (
            <div
              key={item.key}
              onClick={() => {
                if (item.key === 'color') { setPickerOpen(true); setMenuOpen(false); }
                else if (item.key === 'rename') { startRename(); }
                else if (item.key === 'delete') { setMenuOpen(false); onRequestDelete(); }
              }}
              style={{
                padding: '7px 10px',
                fontSize: 13,
                color: item.danger ? '#DC2626' : '#0F1117',
                cursor: 'pointer',
                borderRadius: 6,
                fontFamily: 'var(--font-ui)',
                transition: 'background 0.12s',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = '#F5F6F8')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >{item.label}</div>
          ))}
        </div>
      )}

      {/* Color picker popover */}
      {pickerOpen && !isUnassigned && (
        <div style={{
          position: 'absolute', top: '100%', right: 8, marginTop: 4,
          background: '#FFFFFF',
          border: '1px solid #DDE0E6',
          borderRadius: 8,
          boxShadow: '0 4px 16px rgba(15,17,23,0.12)',
          padding: 10, zIndex: 50,
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 28px)',
          gridTemplateRows: 'repeat(4, 28px)',
          gap: 8,
        }}>
          {DAGRUN_COLORS.map(swatch => {
            const active = !!color && color.toLowerCase() === swatch.hex.toLowerCase();
            return (
              <button
                key={swatch.hex}
                type="button"
                title={`${swatch.label} — ${swatch.hex}`}
                onClick={() => pickColor(swatch.hex)}
                style={{
                  width: 28, height: 28,
                  borderRadius: 6,
                  background: swatch.hex,
                  border: 'none',
                  cursor: 'pointer',
                  outline: active ? '2px solid #FFFFFF' : 'none',
                  outlineOffset: active ? 2 : 0,
                  boxShadow: active ? '0 0 0 3px #0E7490' : 'none',
                  padding: 0,
                  transition: 'transform 0.1s',
                }}
                onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.08)')}
                onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── CompletedSection ──────────────────────────────────────────────────
// Collapsed by default. When expanded, renders done tasks using the standard
// TaskCard with opacity 0.65 on the wrapper. Done tasks are NOT draggable —
// the section sits outside the Droppable, so they're plain DOM nodes.

interface CompletedSectionProps {
  items: Task[];
  onTaskClick: (t: Task) => void;
  pca: IPublicClientApplication;
  phaseNameById: Record<string, string>;
  onTaskDeleted?: (taskId: string) => void;
}

function CompletedSection({ items, onTaskClick, pca, phaseNameById, onTaskDeleted }: CompletedSectionProps) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%',
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '8px 12px',
          background: 'transparent',
          border: 'none',
          fontFamily: 'var(--font-ui)',
          fontSize: 11, fontWeight: 600,
          color: '#5A6272',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span style={{
          display: 'inline-block',
          transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 0.15s',
          fontSize: 9,
          lineHeight: 1,
        }}>▶</span>
        <span>{items.length} Completed</span>
      </button>
      {open && (
        <div style={{ overflowY: 'auto', overflowX: 'hidden', maxHeight: '60vh', padding: '0 8px 8px' }}>
          {items.map(t => (
            <div key={t.dagrun_pm_taskid} style={{ opacity: 0.65 }}>
              <TaskCard
                task={t}
                onClick={onTaskClick}
                pca={pca}
                phaseName={t._dagrun_pm_phaseid_value ? phaseNameById[t._dagrun_pm_phaseid_value] : undefined}
                onDeleted={onTaskDeleted}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── StackedSidebar ────────────────────────────────────────────────────
// One stacked sidebar column at the left edge of the board, the same
// 264px width as a regular collection column. Each row inside is one
// "quiet" collection (zero active tasks). A row shows the collection
// name + a "+ Add Task" button. Clicking Add Task opens NewTaskModal
// scoped to that collection — when the task is created, the collection
// stops being quiet and renders as a regular column on the next reload.
// The sidebar is NOT compact, NOT rotated, NOT collapsed.

interface StackedSidebarProps {
  collections: Collection[];
  byCollection: Record<string, Task[]>;
  onAddTask: (collectionId: string, count: number) => void;
  onTaskClick: (t: Task) => void;
  pca: IPublicClientApplication;
  phaseNameById: Record<string, string>;
  onTaskDeleted?: (taskId: string) => void;
}

function StackedSidebar({ collections, byCollection, onAddTask, onTaskClick, pca, phaseNameById, onTaskDeleted }: StackedSidebarProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  return (
    <div style={{
      width: 264, flexShrink: 0,
      display: 'flex', flexDirection: 'column',
      background: '#FFFFFF',
      borderRadius: 8,
      boxShadow: '0 1px 3px rgba(15,17,23,0.06)',
      overflow: 'hidden',
      maxHeight: 'calc(100vh - 240px)',
    }}>
      <div style={{
        padding: '10px 12px',
        background: 'linear-gradient(135deg, #475569, #334155)',
        color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <span style={{
          fontFamily: 'var(--font-ui)',
          fontSize: 11, fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: '0.08em',
        }}>Quiet</span>
        <span style={{
          background: 'rgba(255,255,255,0.22)',
          fontSize: 10, padding: '1px 8px', borderRadius: 10,
          fontFamily: 'var(--font-mono)',
        }}>{collections.length}</span>
      </div>
      <div style={{ padding: 8, flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}>
        {collections.map(c => {
          const items = byCollection[c.dagrun_pm_collectionid] ?? [];
          const accent = c.dagrun_pm_color || '#0E7490';
          const expanded = expandedId === c.dagrun_pm_collectionid;
          return (
            <div key={c.dagrun_pm_collectionid} style={{
              border: '0.5px solid var(--border-light)',
              borderRadius: 6, overflow: 'hidden',
              marginBottom: 6,
            }}>
              <Droppable droppableId={c.dagrun_pm_collectionid}>
                {(dropProvided, dropSnapshot) => (
                  <div
                    ref={dropProvided.innerRef}
                    {...dropProvided.droppableProps}
                    style={{ background: dropSnapshot.isDraggingOver ? '#D4EEF5' : 'transparent', transition: 'background 0.12s' }}
                  >
                    <button
                      type="button"
                      onClick={() => setExpandedId(expanded ? null : c.dagrun_pm_collectionid)}
                      style={{
                        width: '100%',
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '8px 10px',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        fontFamily: 'var(--font-ui)',
                        fontSize: 13, fontWeight: 500,
                        color: 'var(--ink-2)',
                        textAlign: 'left',
                      }}
                    >
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: accent, flexShrink: 0 }} />
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.dagrun_pm_name}</span>
                      <span style={{ fontSize: 10, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)' }}>{items.length}</span>
                    </button>
                    <div style={{ padding: '0 8px 8px' }}>
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); onAddTask(c.dagrun_pm_collectionid, items.length); }}
                        style={{
                          width: '100%',
                          padding: '5px 8px',
                          background: '#D4EEF5',
                          border: '1px solid #0E7490', borderRadius: 4,
                          fontFamily: 'var(--font-ui)',
                          fontSize: 12, color: '#0E7490',
                          cursor: 'pointer',
                        }}
                      >+ Add Task</button>
                    </div>
                    {expanded && items.length > 0 && (
                      <div style={{ padding: '0 8px 8px', borderTop: '0.5px solid var(--border-light)' }}>
                        {items.map(t => (
                          <div key={t.dagrun_pm_taskid} style={{ opacity: 0.65, marginTop: 6 }}>
                            <TaskCard
                              task={t}
                              onClick={onTaskClick}
                              pca={pca}
                              phaseName={t._dagrun_pm_phaseid_value ? phaseNameById[t._dagrun_pm_phaseid_value] : undefined}
                              onDeleted={onTaskDeleted}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ display: 'none' }}>{dropProvided.placeholder}</div>
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ── VirtualBoard ──────────────────────────────────────────────────────
// Read-only board that groups tasks by a non-collection attribute (Assignee,
// Priority, or Phase). No drag-and-drop — dropping into these columns would be
// ambiguous (what does it mean to drop a task "into" the High priority column?).
// Users switch back to Status to reorder.

interface VirtualBoardProps {
  tasks: Task[];
  phases: Phase[];
  groupBy: Exclude<GroupBy, 'status'>;
  onTaskClick: (t: Task) => void;
  pca: IPublicClientApplication;
  phaseNameById: Record<string, string>;
  onTaskDeleted?: (taskId: string) => void;
}

const PRIORITY_GROUPS: { key: string; label: string; match: (t: Task) => boolean; color: string }[] = [
  { key: '4', label: 'Critical', match: t => t.dagrun_pm_priority === 100000003, color: '#DC2626' },
  { key: '3', label: 'High',     match: t => t.dagrun_pm_priority === 100000002, color: '#D97706' },
  { key: '2', label: 'Medium',   match: t => t.dagrun_pm_priority === 100000001, color: '#0E7490' },
  { key: '1', label: 'Low',      match: t => t.dagrun_pm_priority === 100000000, color: '#5A6272' },
  { key: '0', label: 'None',     match: t => t.dagrun_pm_priority == null,       color: '#9198A8' },
];

function VirtualBoard({ tasks, phases, groupBy, onTaskClick, pca, phaseNameById, onTaskDeleted }: VirtualBoardProps) {
  const groups: { key: string; label: string; accent: string; items: Task[] }[] = [];

  if (groupBy === 'assignee') {
    const byAssignee = new Map<string, { name: string; items: Task[] }>();
    tasks.forEach(t => {
      const oid  = t.dagrun_pm_assigneeid ?? '__unassigned';
      const name = t.dagrun_pm_assigneename ?? 'Unassigned';
      if (!byAssignee.has(oid)) byAssignee.set(oid, { name, items: [] });
      byAssignee.get(oid)!.items.push(t);
    });
    const entries = [...byAssignee.entries()].sort((a, b) => a[1].name.localeCompare(b[1].name));
    entries.forEach(([oid, v]) => groups.push({ key: oid, label: v.name, accent: '#0E7490', items: v.items }));
  } else if (groupBy === 'priority') {
    PRIORITY_GROUPS.forEach(p => {
      const items = tasks.filter(p.match);
      if (items.length > 0) groups.push({ key: p.key, label: p.label, accent: p.color, items });
    });
  } else if (groupBy === 'phase') {
    phases.forEach(ph => {
      const items = tasks.filter(t => t._dagrun_pm_phaseid_value === ph.dagrun_pm_phaseid);
      groups.push({ key: ph.dagrun_pm_phaseid, label: ph.dagrun_pm_name, accent: '#0E7490', items });
    });
    const unphased = tasks.filter(t => !t._dagrun_pm_phaseid_value);
    if (unphased.length > 0) groups.push({ key: '__nophase', label: 'No phase', accent: '#9198A8', items: unphased });
  }

  return (
    <div style={{ flex: 1, display: 'flex', gap: 12, padding: 14, overflowX: 'auto', alignItems: 'flex-start' }}>
      {groups.map(g => (
        <div key={g.key} style={{
          // Match the Status-mode column width (264 = 240 card + 12px×2 padding).
          width: 264, flexShrink: 0,
          display: 'flex', flexDirection: 'column',
          maxHeight: 'calc(100vh - 280px)',
          background: '#FFFFFF',
          borderRadius: 8,
          boxShadow: '0 1px 3px rgba(15,17,23,0.06)',
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '10px 12px',
            background: `linear-gradient(135deg, ${g.accent}, ${darkenHex(g.accent, 15)})`,
            color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          }}>
            <span style={{
              fontFamily: 'var(--font-ui)',
              fontSize: 11, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.08em',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              flex: 1, minWidth: 0,
            }}>{g.label}</span>
            <span style={{
              background: 'rgba(255,255,255,0.22)',
              fontSize: 10, padding: '1px 8px', borderRadius: 10,
              fontFamily: 'var(--font-mono)',
            }}>{g.items.length}</span>
          </div>
          <div style={{ padding: 8, flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
            {g.items.length === 0 ? (
              <div style={{ padding: '16px 8px', textAlign: 'center', fontFamily: 'var(--font-ui)', fontSize: 11, color: '#B8BEC9' }}>
                No tasks
              </div>
            ) : g.items.map(t => (
              <TaskCard
                key={t.dagrun_pm_taskid}
                task={t}
                onClick={onTaskClick}
                pca={pca}
                phaseName={t._dagrun_pm_phaseid_value ? phaseNameById[t._dagrun_pm_phaseid_value] : undefined}
                onDeleted={onTaskDeleted}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
